/* eslint-disable class-methods-use-this */
/* eslint-disable no-undef */
/* eslint-disable no-bitwise */
import EoSParticipant from './EoSParticipant';

class EoSCursorSystem {
    #cursor = 0;

    #current_user = null;

    #last_transmit = 0;

    #rotated = false;

    #eos = false;

    #transmit_timeout;

    constructor(jitsiApi, jitsiDiv, canvasDiv) {
        this.jitsiApi = jitsiApi;
        this.jitsiDiv = jitsiDiv;
        this.canvasDiv = canvasDiv;

        // Do nothing if the canvas and jitsi do not share a common parent
        if (this.canvasDiv.parentElement !== this.jitsiDiv.parentElement) {
            console.log("Canvas and Jitsi do not share a common parent")
            return;

        }

        // Register resizing element
        new ResizeObserver(this.recalculate_size.bind(this)).observe(this.canvasDiv.parentElement);

        this.user = {};

        this.conversionFactor = 0.0;

        // Setup styles
        this.canvasDiv.style.pointerEvents = "none";   // Canvas not clickable at start

        this.canvasDiv.width = 1280;   // Canvas draw resolution
        this.canvasDiv.height = 720;

        // Add listener to when the user clicks the canvas
        this.canvasDiv.addEventListener("mousedown", this.canvas_click.bind(this));
        this.canvasDiv.addEventListener("mousemove", this.canvas_mousemove.bind(this));
        this.canvasDiv.addEventListener("mouseup", this.canvas_mouseup.bind(this));

        console.log("EoSCursorSystem created")
        // Add listeners to jitsi
        if (this.jitsiApi) {
            // Add listener to when the current user joins a meeting (so that colors can be assigned)
            this.jitsiApi.addListener("videoConferenceJoined", this.joined.bind(this));

            // Add listener to when someone gives us a cursor update
            this.jitsiApi.addListener("endpointTextMessageReceived", this.receive.bind(this));

            // Add listener to when someone joints the meeting (so we can show them our cursors)
            this.jitsiApi.addListener("participantJoined", this.user_join.bind(this));

            // Add listener to when someone leaves the meeting (so we can remove their cursors)
            this.jitsiApi.addListener("participantLeft", this.user_left.bind(this));
            console.log("EoSCursorSystem jitsi listeners added")
        }
        else {
            this.#current_user = 'abcdef';
            const color = "#492f74";
            this.user[this.#current_user] = new EoSParticipant(this.#current_user, color, 0, 0, 0, 0);
        }

        // Setup cursor selection and dragging
        this.cursor = 0;
        this.dragging = false;

        // Transmit every 5 seconds (limited)
        setInterval(this.transmit.bind(this), 5000, true);

        this.recalculate_size();
        this.draw();
    }

    rotate() {
        this.#rotated = !this.#rotated;
        this.recalculate_size();
        this.draw();
    }

    recalculate_size() {
        const disp = this.canvasDiv.parentElement;
        const screenWidth = disp.offsetWidth;
        const screenHeight = disp.offsetHeight;

        let width;
        let height;

        if (this.#rotated) {
            width = screenHeight;
            height = screenWidth;
        }
        else {
            width = screenWidth;
            height = screenHeight;
        }

        // Calculate largest scaling a 1280x720 image would have inside the rectangle
        const xScale = width / 1280;
        const yScale = height / 720;
        const scale = Math.min(xScale, yScale);

        width = 1280 * scale;
        height = 720 * scale;

        this.canvasDiv.style.width = `${width}px`;
        this.jitsiDiv.style.width = `${width}px`;
        this.canvasDiv.style.height = `${height}px`;
        this.jitsiDiv.style.height = `${height}px`;

        if (this.#rotated) {
            this.canvasDiv.style.transform = 'rotate(90deg)';
            this.jitsiDiv.style.transform = 'rotate(90deg)';
            this.canvasDiv.style.top = `${(screenHeight - width) / 2}px`;
            this.jitsiDiv.style.top = `${(screenHeight - width) / 2}px`;
            this.canvasDiv.style.left = `${(height + screenWidth) / 2}px`;
            this.jitsiDiv.style.left = `${(height + screenWidth) / 2}px`;
        }
        else {
            this.canvasDiv.style.transform = '';
            this.jitsiDiv.style.transform = '';
            this.canvasDiv.style.top = `${(screenHeight - height) / 2}px`;
            this.jitsiDiv.style.top = `${(screenHeight - height) / 2}px`;
            this.canvasDiv.style.left = `${(screenWidth - width) / 2}px`;
            this.jitsiDiv.style.left = `${(screenWidth - width) / 2}px`;
        }
    }

    set_conversion_factor(conversionFactor) {
        // Anything that uses this function is now assumed to be an EoS device
        this.conversionFactor = conversionFactor;
        this.user[this.#current_user].device = "eos";
        this.#eos = true;
        this.transmit(false);
        this.draw();
    }

    draw() {
        if (this.canvasDiv.getContext) {
            const ctx = this.canvasDiv.getContext("2d");

            // Clear Screen
            // eslint-disable-next-line no-undef
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw cursors
            // eslint-disable-next-line no-restricted-syntax
            for (const [, participant] of Object.entries(this.user)) {
                participant.draw(ctx, this.conversionFactor, this.#rotated);
            }
        }
    }

    set cursor(cursor) {
        this.#cursor = cursor;

        // Change whether the canvas is clickable or not
        if (this.#cursor > 0) {
            this.canvasDiv.style.pointerEvents = "auto";
        }
        else {
            this.canvasDiv.style.pointerEvents = "none";
        }
    }

    get cursor() {
        return this.#cursor;
    }

    joined(data) {
        // As the local user joins, set up the variables for that user's cursors
        this.#current_user = data.id;
        const color = this.generateColor(data.displayName);

        this.user[this.#current_user] = new EoSParticipant(this.#current_user, color, 0, 0, 0, 0);

        if (this.#eos) {
            this.user[this.#current_user].device = "eos"
        }
    }

    // eslint-disable-next-line no-unused-vars
    user_join(data) {
        // Send the current cursor to the user who just joined
        if (this.#current_user) {
            // Apparently needs some kind of delay
            setTimeout(() => { this.transmit(false); }, 1000);
        }
    }

    user_left(data) {
        delete this.user[data.id];
        this.draw();
    }

    transmit(limit = true) {
        // No jitsi, no channel to transmit to
        if (!this.jitsiApi)
            return;

        // No participants, no one to send to
        if (this.jitsiApi.getNumberOfParticipants() < 2)
            return;

        // Clear any timeouts
        clearTimeout(this.#transmit_timeout);

        // Do nothing when we haven't joined a meeting yet
        if (!this.#current_user)
            return;

        // Get current time
        const time = +new Date();

        if (!limit || time - this.#last_transmit > 250) // Limit transmissions to every 250ms
        {
            this.#last_transmit = time;
            const user = this.user[this.#current_user];
            const userJson = user.json_dict;

            if (this.#eos) {
                userJson["conversion-factor"] = this.conversionFactor;
                userJson.device = "eos";

                if (this.jitsiApi) {
                    // Set ourselves as the camera to look at
                    this.jitsiApi.executeCommand('setLargeVideoParticipant', this.#current_user, 'camera');
                    this.jitsiApi.pinParticipant(this.#current_user, 'camera');
                }
            }

            if (this.jitsiApi) {
                this.jitsiApi.executeCommand("sendEndpointTextMessage", "", JSON.stringify(userJson));
            }
        }
        // Limited by transmit, transmit again after 250ms
        else if (limit) {
            this.#transmit_timeout = setTimeout(() => { this.transmit(false); }, 250);
        }
    }

    receive(data) {
        // TODO check if this is actually a cursor update
        const jsonData = JSON.parse(data.data.eventData.text);
        this.user[jsonData.id] = new EoSParticipant(jsonData.id, jsonData.color, jsonData.cursors[0].x, jsonData.cursors[0].y, jsonData.cursors[1].x, jsonData.cursors[1].y);

        if (jsonData.device === "eos") {
            this.conversionFactor = jsonData["conversion-factor"] ?? this.conversionFactor;

            if (this.jitsiApi) {
                // Set this person as the video to see
                this.jitsiApi.executeCommand('setLargeVideoParticipant', jsonData.id, 'camera');
                this.jitsiApi.pinParticipant(jsonData.id, 'camera');
            }
        }

        this.draw();
    }

    canvas_mouseup() {
        // When the user releases the mouse, transmit that last position too
        this.dragging = false;
        this.transmit(false);
    }

    canvas_mousemove(mouseEv) {
        // Do nothing when we haven't joined a meeting yet
        if (!this.#current_user)
            return;

        // Update cursor positions (scaled to the canvas's native resolution)
        if (this.dragging) {
            const user = this.user[this.#current_user];

            if (this.#cursor === 1) {
                user.c1x = mouseEv.offsetX * canvas.width / canvas.clientWidth;
                user.c1y = mouseEv.offsetY * canvas.height / canvas.clientHeight;
                this.draw();
                this.transmit();
            }
            if (this.#cursor === 2) {
                user.c2x = mouseEv.offsetX * canvas.width / canvas.clientWidth;
                user.c2y = mouseEv.offsetY * canvas.height / canvas.clientHeight;
                this.draw();
                this.transmit();
            }
        }
    }

    canvas_click(mouseEv) {
        this.dragging = true;

        // Do nothing when we haven't joined a meeting yet
        if (!this.#current_user)
            return;

        // Check if near cursor, if so, then switch to that
        const user = this.user[this.#current_user];
        const mousex = mouseEv.offsetX * canvas.width / canvas.clientWidth;
        const mousey = mouseEv.offsetY * canvas.height / canvas.clientHeight;

        // Check if near cursor 1
        if ((mousex - user.c1x) ** 2 + (mousey - user.c1y) ** 2 < 121) {
            this.#cursor = 1;
        }
        // Check if near cursor 2
        else if (Math.abs(mousex - user.c2x) < 10 && Math.abs(mousey - user.c2y) < 10) {
            this.#cursor = 2;
        }

        this.canvas_mousemove(mouseEv);
    }

    generateColor(name) {
        const colors = [
            '#f86c66',
            '#7366f8',
            '#66f8ed',
            '#66f866',
            '#f866e2',
            '#f8ef66',
            '#a5a6b4',
            '#f00074',
            '#567b62',
            '#2a7ad4',
        ];

        const hash = hashCode(name) >>> 0;

        return colors[hash % colors.length];
    }
}

// Copied from https://stackoverflow.com/a/8831937
function hashCode(str) {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i += 1) {
        const chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}
export default EoSCursorSystem;
